import { useDebouncedValue } from '@app/core/hooks/useDebouncedValue';
import { Chip } from '@mui/material';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { getGipStationIds } from '../get-gip-station-ids';
import { useGipAdvertisersQuery } from '../hooks/useGipAdvertisersQuery';
import { GipReportValues } from '../types';
import { StyledAutocompleteField } from './StyledAutocompleteField';

/** Advertisers autocomplete with debounced server-side search. */
export function AdvertisersFieldMui() {
  const { $t } = useIntl();
  const { values, setFieldValue, setFieldTouched, isSubmitting } =
    useFormikContext<GipReportValues>();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm);

  const queryEnabled = debouncedSearchTerm.length >= 2;

  const query = useGipAdvertisersQuery(
    {
      from: values.from,
      to: values.to,
      stations: getGipStationIds(values),
      search: debouncedSearchTerm,
    },
    queryEnabled,
  );

  const options = useMemo(() => {
    return query.data?.map((name) => ({ name })) || [];
  }, [query.data]);

  const selectedValues = useMemo(() => {
    return values.advertisers || [];
  }, [values.advertisers]);

  const handleChange = (_event: React.SyntheticEvent, newValue: { name: string }[]) => {
    setFieldValue('advertisers', newValue);
    setFieldTouched('advertisers');
  };

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setSearchTerm(newInputValue);
  };

  return (
    <StyledAutocompleteField
      name="advertisers"
      label={$t({
        id: 'audit.gipReport.form.advertisers',
        defaultMessage: 'Anunciantes',
      })}
      placeholder={$t({
        id: 'audit.gipReport.form.allAdvertisers',
        defaultMessage: 'Todos los anunciantes',
      })}
      helperText={
        searchTerm.length > 0 && searchTerm.length < 2
          ? $t({
              id: 'audit.gipReport.form.typeToSearch',
              defaultMessage: 'Ingrese al menos 2 caracteres iniciales',
            })
          : undefined
      }
      multiple
      options={options}
      value={selectedValues}
      onChange={handleChange}
      onInputChange={handleInputChange}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.name === value.name}
      loading={query.isFetching}
      readOnly={isSubmitting}
      loadingText={$t({
        id: 'common.loading',
        defaultMessage: 'Cargando...',
      })}
      noOptionsText={
        searchTerm.length < 2
          ? $t({
              id: 'audit.gipReport.form.typeToSearch',
              defaultMessage: 'Ingrese al menos 2 caracteres iniciales',
            })
          : $t({
              id: 'common.noOptions',
              defaultMessage: 'No se encontraron resultados',
            })
      }
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip label={option.name} {...getTagProps({ index })} key={option.name} size="small" />
        ))
      }
      filterOptions={(options) => options} // Disable client-side filtering since we're doing server-side search
      freeSolo={false}
    />
  );
}
